import { useCallback, useEffect, useRef, useState } from 'react';
import { appendDictation } from '../utils/dictation';

const DRAFT_PREFIX = 'omnis:draft:';
const DRAFT_EVENT = 'omnis:draft-changed';

const draftKey = (conversationId) => `${DRAFT_PREFIX}${conversationId || '__new__'}`;

const safeParse = (raw) => {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (_) {
        return null;
    }
};

const emptyDraft = () => ({
    text: '',
    attachedFiles: [],
    replyingToMessages: [],
    voiceSegments: [],
});

const isDraftNonEmpty = (draft) => {
    if (!draft) return false;
    if ((draft.text || '').trim().length > 0) return true;
    if (Array.isArray(draft.attachedFiles) && draft.attachedFiles.length > 0) return true;
    if (Array.isArray(draft.replyingToMessages) && draft.replyingToMessages.length > 0) return true;
    if (Array.isArray(draft.voiceSegments) && draft.voiceSegments.length > 0) return true;
    return false;
};

// `Blob` + `blobUrl` are in-memory only — they can't be serialised to
// localStorage and the URL is tied to this document's lifetime. We strip
// them on save; playback after hydrate uses the server `audioUrl` once a
// segment is ready, and shows a spinner for segments still transcribing.
const sanitizeVoiceSegments = (segments) => {
    if (!Array.isArray(segments)) return [];
    return segments.map((s) => ({
        id: s.id,
        duration: s.duration,
        mimeType: s.mimeType,
        status: s.status,
        audioUrl: s.audioUrl,
        gcsPath: s.gcsPath,
        transcription: s.transcription,
        // The user's pre-send correction of the STT, kept separate from the raw
        // audio record so an in-progress edit survives navigating away and back
        // — and so an origin-bound send flushed from the draft still runs on the
        // corrected text (transcription edit #252).
        editedTranscription: typeof s.editedTranscription === 'string' ? s.editedTranscription : null,
        showTranscription: !!s.showTranscription,
        error: s.error || null,
        // Tag with the originating convo so the in-flight transcription
        // can patch the right draft even if the user navigated away.
        originConversationId: s.originConversationId || null,
    }));
};

const notifyDraftChange = (conversationId) => {
    try {
        window.dispatchEvent(new CustomEvent(DRAFT_EVENT, { detail: { conversationId } }));
    } catch (_) { /* ignore */ }
};

const loadFromStorage = (conversationId) => (
    safeParse(localStorage.getItem(draftKey(conversationId))) || emptyDraft()
);

/**
 * Patch a single voice-segment row inside the persisted draft for a
 * specific conversation. Used by in-flight transcription resolvers when
 * the user has navigated away from the conversation where they accepted
 * the segment — the React `setVoiceSegments` wouldn't reach the right
 * conversation anymore, so we update storage directly and fire the
 * draft-changed event so the sidebar's orange dot stays accurate.
 *
 * Returns `true` if the segment was found + patched, `false` otherwise.
 */
export function patchVoiceSegmentInDraft(conversationId, segmentId, patch) {
    if (!segmentId || !patch) return false;
    const draft = loadFromStorage(conversationId);
    const segs = Array.isArray(draft.voiceSegments) ? draft.voiceSegments : [];
    const idx = segs.findIndex((s) => s.id === segmentId);
    if (idx < 0) return false;
    const next = sanitizeVoiceSegments(
        segs.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    );
    const nextDraft = { ...draft, voiceSegments: next };
    try {
        if (isDraftNonEmpty(nextDraft)) {
            localStorage.setItem(draftKey(conversationId), JSON.stringify(nextDraft));
        } else {
            localStorage.removeItem(draftKey(conversationId));
        }
    } catch (_) { /* ignore */ }
    notifyDraftChange(conversationId);
    return true;
}

// Persist a full draft object for a specific conversation (or remove the key
// when the draft is empty), then fire the draft-changed event so the sidebar's
// unfinished-draft dot stays accurate.
const writeDraft = (conversationId, draft) => {
    try {
        if (isDraftNonEmpty(draft)) {
            localStorage.setItem(draftKey(conversationId), JSON.stringify(draft));
        } else {
            localStorage.removeItem(draftKey(conversationId));
        }
    } catch (_) { /* ignore */ }
    notifyDraftChange(conversationId);
};

/**
 * Append an attachment descriptor to a SPECIFIC conversation's persisted draft.
 * Sibling of patchVoiceSegmentInDraft for dropped-file uploads: when a bulk
 * upload batch is started in conversation A and the user navigates away mid-
 * batch, the live React `addAttachedFile` would land later pills on whatever
 * conversation is now on screen ("cross-conversation upload bleeding"). Writing
 * straight to A's draft keeps the batch bound to A. De-dupes by tempId.
 */
export function addAttachedFileToDraft(conversationId, descriptor) {
    if (!descriptor) return false;
    const draft = loadFromStorage(conversationId);
    const files = Array.isArray(draft.attachedFiles) ? draft.attachedFiles : [];
    if (descriptor.tempId && files.some((f) => f.tempId === descriptor.tempId)) return false;
    writeDraft(conversationId, { ...draft, attachedFiles: [...files, descriptor] });
    return true;
}

/**
 * Patch a single attachment descriptor (matched by tempId) inside a specific
 * conversation's persisted draft — the upload-completion sibling of
 * addAttachedFileToDraft, for when an upload resolves after the user has left
 * the originating conversation. Returns `true` if the pill was found + patched.
 */
export function patchAttachedFileInDraft(conversationId, tempId, patch) {
    if (!tempId || !patch) return false;
    const draft = loadFromStorage(conversationId);
    const files = Array.isArray(draft.attachedFiles) ? draft.attachedFiles : [];
    const idx = files.findIndex((f) => f.tempId === tempId);
    if (idx < 0) return false;
    const nextFiles = files.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    writeDraft(conversationId, { ...draft, attachedFiles: nextFiles });
    return true;
}

/**
 * Remove an attachment descriptor (by tempId) from a specific conversation's
 * persisted draft — used to cancel a parked upload that belongs to a
 * conversation the user has navigated away from.
 */
export function removeAttachedFileFromDraft(conversationId, tempId) {
    if (!tempId) return false;
    const draft = loadFromStorage(conversationId);
    const files = Array.isArray(draft.attachedFiles) ? draft.attachedFiles : [];
    const nextFiles = files.filter((f) => f.tempId !== tempId);
    if (nextFiles.length === files.length) return false;
    writeDraft(conversationId, { ...draft, attachedFiles: nextFiles });
    return true;
}

/**
 * Read a specific conversation's persisted draft (never null — an empty draft
 * shape is returned when the key is absent). Used by the origin-bound send flush
 * to reconstruct a queued message's payload from the origin's draft when the
 * user has navigated away from the conversation they hit Send in.
 */
export function readConversationDraft(conversationId) {
    return loadFromStorage(conversationId);
}

/**
 * Dictation (#541): append transcribed text to a SPECIFIC conversation's
 * persisted draft text — for a dictation that settles after the user switched
 * to another conversation (or left the shell). Uses the same append rule as
 * the live composer (`utils/dictation.appendDictation`). Returns the new text.
 */
export function appendTextToDraft(conversationId, text) {
    const draft = loadFromStorage(conversationId);
    const nextText = appendDictation(draft.text || '', text);
    if (nextText === (draft.text || '')) return nextText;
    writeDraft(conversationId, { ...draft, text: nextText });
    return nextText;
}

/** Wipe a specific conversation's persisted draft (by explicit id). */
export function clearConversationDraft(conversationId) {
    try {
        localStorage.removeItem(draftKey(conversationId));
    } catch (_) { /* ignore */ }
    notifyDraftChange(conversationId);
}

/**
 * Move a draft from one conversation key to another by EXPLICIT ids — the id-
 * agnostic sibling of the hook's `renameDraft` (which only moves from the
 * currently-loaded conversation). Used when an off-screen `__new__` send has to
 * mint a conversation without the user's view being on `__new__`.
 */
export function moveConversationDraft(fromId, toId) {
    if (!toId || fromId === toId) return;
    try {
        const raw = localStorage.getItem(draftKey(fromId));
        if (raw && isDraftNonEmpty(safeParse(raw))) {
            localStorage.setItem(draftKey(toId), raw);
        }
        localStorage.removeItem(draftKey(fromId));
        notifyDraftChange(fromId);
        notifyDraftChange(toId);
    } catch (_) { /* ignore */ }
}

/** Inspect localStorage to find conversations with non-empty drafts. */
export function listConversationsWithDrafts() {
    const ids = new Set();
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key || !key.startsWith(DRAFT_PREFIX)) continue;
            const draft = safeParse(localStorage.getItem(key));
            if (isDraftNonEmpty(draft)) {
                ids.add(key.slice(DRAFT_PREFIX.length));
            }
        }
    } catch (_) { /* ignore */ }
    return ids;
}

/**
 * Per-conversation draft persistence. The Main Office input's full state
 * (text, attached-file descriptors, reply selections) is saved to
 * localStorage keyed by conversation ID so switching conversations — or
 * even reloading the page — never loses the user's in-progress thought.
 *
 * `conversationId` may be `null` (no conversation loaded yet) — those drafts
 * are saved under a shared '__new__' key so a thought typed before the first
 * conversation exists isn't lost when one is created.
 *
 * Returns:
 *   hydrate()    — read fresh draft from storage (for the current conv id);
 *                  call this on convo switch to re-populate the input.
 *   saveDraft(p) — merge patch into the current draft and persist.
 *   clearDraft() — wipe the current draft (called on successful send).
 *   renameDraft  — move a draft from __new__ → real conv id on first send.
 */
export function useConversationDraft(conversationId) {
    // Keep a ref to the id so callbacks can read it without being recreated.
    const convIdRef = useRef(conversationId);
    useEffect(() => { convIdRef.current = conversationId; }, [conversationId]);

    const hydrate = useCallback(() => loadFromStorage(convIdRef.current), []);

    const saveDraft = useCallback((patch) => {
        const id = convIdRef.current;
        const existing = loadFromStorage(id);
        const next = { ...existing, ...patch };
        if (Object.prototype.hasOwnProperty.call(patch || {}, 'voiceSegments')) {
            next.voiceSegments = sanitizeVoiceSegments(patch.voiceSegments);
        }
        try {
            if (isDraftNonEmpty(next)) {
                localStorage.setItem(draftKey(id), JSON.stringify(next));
            } else {
                localStorage.removeItem(draftKey(id));
            }
        } catch (_) { /* ignore */ }
        notifyDraftChange(id);
    }, []);

    const clearDraft = useCallback(() => {
        const id = convIdRef.current;
        try {
            localStorage.removeItem(draftKey(id));
        } catch (_) { /* ignore */ }
        notifyDraftChange(id);
    }, []);

    const renameDraft = useCallback((newConversationId) => {
        const oldId = convIdRef.current;
        if (!newConversationId || newConversationId === oldId) return;
        try {
            const raw = localStorage.getItem(draftKey(oldId));
            if (raw && isDraftNonEmpty(safeParse(raw))) {
                localStorage.setItem(draftKey(newConversationId), raw);
            }
            localStorage.removeItem(draftKey(oldId));
            notifyDraftChange(oldId);
            notifyDraftChange(newConversationId);
        } catch (_) { /* ignore */ }
    }, []);

    return {
        hydrate,
        saveDraft,
        clearDraft,
        renameDraft,
    };
}

/**
 * Observe the set of conversations currently holding a non-empty draft —
 * the sidebar uses this to show a pulsing dot next to each unfinished row.
 */
export function useDraftedConversationIds() {
    const [ids, setIds] = useState(() => listConversationsWithDrafts());

    useEffect(() => {
        const recompute = () => setIds(listConversationsWithDrafts());
        window.addEventListener(DRAFT_EVENT, recompute);
        window.addEventListener('storage', recompute);
        return () => {
            window.removeEventListener(DRAFT_EVENT, recompute);
            window.removeEventListener('storage', recompute);
        };
    }, []);

    return ids;
}
